export type AdminAnalyticsPropertyValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | AdminAnalyticsPropertyValue[]
  | { [key: string]: AdminAnalyticsPropertyValue }

export type AdminAnalyticsProperties = Record<
  string,
  AdminAnalyticsPropertyValue
>

export type AnalyticsApplication = 'web' | 'app'
export type AnalyticsSurface = 'admin' | 'mobile' | 'system'

export type AdminAnalyticsEventPropertiesByName = {
  admin_dashboard_viewed: {
    page: string
    role: string | null
    user_type?: string | null
  }
  challenge_created: {
    challenge_id: number | null
    behavior: string | null
    team_count: number
    reward_count: number
  }
  challenge_removed: {
    challenge_id: number
    behavior: string | null
    team_count: number
    removal_mode: 'archived' | 'deleted'
  }
  challenge_updated: {
    challenge_id: number
    behavior: string | null
    team_count: number
    reward_count: number
  }
  approval_flow_toggled: {
    flow: 'compensations_validation' | 'statements_approval'
    enabled: boolean
  }
  approval_steps_saved: {
    flow: 'compensations_validation' | 'statements_approval'
    step_count: number
    removed_step_count: number
    approver_types: string[]
    condition_types: string[]
    has_specific_user: boolean
    adjustable_step_count: number
    required_step_count: number
  }
  data_import_triggered: {
    table_name: string
    task_id: string
    kpis_table_id?: number | null
    custom_record_table_id?: number | null
  }
  business_unit_assignments_updated: {
    business_unit_id: number | null
    business_unit_cost_center_id: number | null
    profile_count: number
    effective_from_date: string
    effective_to_date: string | null
    is_update: boolean
  }
  business_unit_created: {
    business_unit_id: number
    business_unit_name: string
  }
  business_unit_deleted: {
    business_unit_id: number
  }
  business_unit_updated: {
    business_unit_id: number
    business_unit_name: string
  }
  compensation_rule_dry_run_completed: {
    compensation_rule_id: number
    team_count: number
    profile_count: number
    item_count: number
    start_date: string
    end_date: string
  }
  compensation_rule_archived: {
    compensation_rule_id: number
    plan_id: number | null
  }
  compensation_rule_cloned: {
    compensation_rule_id: number
    plan_id: number | null
  }
  compensation_rule_deleted: {
    compensation_rule_id: number
    plan_id: number | null
  }
  compensation_rule_unarchived: {
    compensation_rule_id: number
    plan_id: number | null
  }
  compensation_rule_version_created: {
    compensation_rule_id: number
    compensation_rule_version_id: number | null
    active_period_start: string
    active_period_end: string | null
    is_tvp_based: boolean
    visible_if_scheduled: boolean
  }
  compensation_rule_draft_saved: {
    compensation_rule_id: number
    plan_id: number | null
    rule_label: string | null
  }
  compensation_rule_overridden: {
    compensation_rule_id: number
    plan_id: number | null
    rule_label: string | null
  }
  compensation_rule_published: {
    compensation_rule_id: number
    plan_id: number | null
    rule_label: string | null
    method: string | null
    start_date: string | null
  }
  compensation_rule_scheduled: {
    compensation_rule_id: number
    plan_id: number | null
    rule_label: string | null
    start_date: string | null
  }
  compensation_rule_version_deactivated: {
    compensation_rule_version_id: number
    compensation_rule_id: number | null
    end_date: string
  }
  compensation_rule_version_deleted: {
    compensation_rule_version_id: number
    compensation_rule_id: number | null
  }
  metric_saved: {
    metric_id: number | null
    metric_label: string
    is_update: boolean
    source: string
  }
  issue_reported: {
    issue_id: number
    entity_type: 'payout' | 'statement'
    payout_id: number | null
    statement_id: number | null
    has_comment: boolean
    mention_count: number
    flipper_opted_out: boolean
    slack_available: boolean
  }
  issue_resolved: {
    issue_id: number
    entity_type: 'payout' | 'statement'
    payout_id: number | null
    statement_id: number | null
    has_comment: boolean
    mention_count: number
    slack_available: boolean
  }
  integration_connected: {
    provider: string
  }
  integration_disconnected: {
    provider: string
  }
  integration_metadata_updated: {
    provider: string
    tables_to_sync_count: number
    mapped_entity_count: number
    has_user_mapping: boolean
    has_pipeline_config: boolean
    has_sync_start_date: boolean
  }
  integration_sync_started: {
    integration_kind: 'crm' | 'billing'
    provider: string
    sync_count: number
    sync_names: string[]
  }
  integration_sync_triggered: {
    integration_kind: 'crm' | 'billing'
    provider: string
    sync_count: number
    sync_names: string[]
  }
  manual_payout_saved: {
    payout_id: number | null
    statement_id: number | null
    profile_id: string
    payout_kind: 'compensation' | 'deduction'
    compensation_type: string | null
    is_update: boolean
    value: number
    category_id: number | null
  }
  metric_archived: {
    metric_id: number
    metric_label: string
  }
  motivational_badge_assigned: {
    badge_id: number
    profile_id: string
  }
  motivational_badge_assignment_dismissed: {
    badge_id: number | null
    issued_badge_id: string | null
    profile_id: string | null
  }
  motivational_badge_assignment_restored: {
    badge_id: number | null
    issued_badge_id: string
    profile_id: string | null
  }
  motivational_badge_deleted: {
    badge_id: number
    badge_name: string
  }
  payout_deleted: {
    payout_ids: number[]
    payout_count: number
    source: 'row_action' | 'bulk_action'
  }
  payout_adjustment_created: {
    payout_id: number
    payout_adjustment_id: number | null
    adjusted_value: number
    also_validated: boolean
    has_comment: boolean
    mention_count: number
  }
  payout_discrepancy_applied: {
    payout_discrepancy_id: number
    payout_id: number | null
    discrepancy_type: string | null
    action: string
  }
  payout_discrepancy_dismissed: {
    payout_discrepancy_ids: number[]
    discrepancy_count: number
    discrepancy_types: string[]
  }
  payout_discrepancy_statement_applied: {
    payout_discrepancy_id: number
    payout_id: number
    calculated_statement_id: number
    existing_statement_id: number | null
    reopened_approved_statement: boolean
  }
  payout_discrepancy_validation_reset: {
    payout_discrepancy_id: number
    payout_id: number
    calculated_value: number | null
    reopened_approved_statement: boolean
    has_installments: boolean
  }
  payout_updates_on_hold_previewed: {
    selected_update_count: number
    group_by: 'change' | 'statement'
    visible_statuses: string[]
  }
  payout_updates_on_hold_grouped: {
    group_by: 'change' | 'statement'
  }
  payout_updates_on_hold_filtered: {
    visible_statuses: string[]
  }
  payout_updates_on_hold_resolved: {
    selected_update_count: number
    kept_count: number
    updated_count: number
    adjustment_count: number
    validation_reset_count: number
  }
  payout_run_triggered: {
    status: 'completed'
  }
  payouts_generated: {
    payout_count: number
    rule_count: number
    profile_count: number
    source_type:
      | 'deals'
      | 'activities'
      | 'invoices'
      | 'kpis'
      | 'line_items'
      | 'deal_line_items'
      | 'custom_records'
      | 'period'
  }
  payout_statement_assignment_updated: {
    payout_id: number
    payout_installment_id: number | null
    action: 'unlock' | 'change'
    statement_date: string
  }
  payout_simulation_completed: {
    source_counts: Record<string, number>
  }
  payouts_reported: {
    payout_count: number
    issue_count: number
    source: string | null
    has_comment: boolean
    mention_count: number
    slack_available: boolean
  }
  payouts_validated: {
    payout_count: number
    source: string | null
    has_comment: boolean
    mention_count: number
  }
  payout_validations_revoked: {
    payout_ids: number[]
    payout_count: number
    source: 'row_action'
  }
  payouts_export_triggered: {
    task_id: string
    payout_count: number
    delimiter: ',' | ';'
  }
  statements_export_triggered: {
    task_id: string
    delimiter: ',' | ';'
    statement_count: number | null
  }
  plan_deleted: {
    plan_id: number
  }
  plan_archived: {
    plan_id: number
    plan_label: string
  }
  plan_documentation_generated: {
    plan_id: number
    include_faq: boolean
    compensation_rule_count: number
  }
  plan_duplicated: {
    source_plan_id: number
    new_plan_id: number | null
  }
  plan_saved: {
    plan_id: number | null
    plan_label: string
    is_update: boolean
    currency: string | null
  }
  plan_unarchived: {
    plan_id: number
    plan_label: string
  }
  statement_approved: {
    statement_id: number
    approved_count: number | null
    has_comment: boolean
    mention_count: number
    unreviewed_discrepancies_count: number | null
  }
  statements_materialized: {
    processed_count: number
    start_date: string
    end_date: string
  }
  statement_status_updated: {
    statement_ids: number[]
    statement_count: number
    status: 'open' | 'in_review' | 'approved' | 'paid'
    source: 'row_action' | 'bulk_action'
  }
  source_record_deleted: {
    table_name: string
    record_ids: Array<string | number>
    record_count: number
  }
  source_record_linked: {
    table_name: 'deals' | 'invoices'
    record_id: number
    linked_record_id: number | null
  }
  source_record_recalculation_requested: {
    table_name: string
    record_id: string | number
  }
  source_record_updated: {
    table_name: string
    record_id: string | number | null
    action: 'updated' | 'overridden' | 'locked'
  }
  target_achievement_saved: {
    target_period_amount_id: number
    profile_id: string | null
    team_id: number | null
    amount: number
    is_update: boolean
    is_team_target: boolean
  }
  target_archived: {
    target_id: number
    target_label: string
  }
  target_saved: {
    target_id: number | null
    target_label: string
    is_update: boolean
    is_manual: boolean
    period: string | null
    scope: 'company' | 'global' | 'team' | 'individual'
  }
  target_currency_backfill_started: {
    currency: string
    task_id: string
    metric_values_to_backfill: number
  }
  team_archived: {
    team_id: number
    team_name: string
  }
  team_created: {
    team_id: number
    team_name: string
    tag_count: number
  }
  team_deleted: {
    team_id: number
    team_name: string
  }
  team_membership_permissions_updated: {
    team_id: number
    profile_id: string
    full_view_access: boolean
  }
  team_membership_removed: {
    team_id: number
    profile_id: string
    role: 'member' | 'manager'
  }
  team_updated: {
    team_id: number
    team_name: string
    tag_count: number
  }
  user_archived: {
    profile_id: string
  }
  user_deactivation_set: {
    profile_ids: string[]
    profile_count: number
    method: 'schedule_deactivation' | 'deactivate_now' | null
    event_date: string
  }
  user_invite_sent: {
    profile_ids: string[]
    invite_count: number
    resend: boolean
  }
  user_profile_updated: {
    profile_id: string
    role: string
    type: string | null
    start_date: string | null
    variable_salary_set: boolean
  }
  challenge_activated: {
    challenge_id: number
    behavior: string | null
    team_count: number
  }
}

export type AdminAnalyticsEventName = keyof AdminAnalyticsEventPropertiesByName

export type AdminAnalyticsEventProperties<
  EventName extends AdminAnalyticsEventName
> = AdminAnalyticsEventPropertiesByName[EventName]

export type AdminAnalyticsGroup = {
  organization: string
}

export type AdminAnalyticsCapture<EventName extends AdminAnalyticsEventName> = {
  distinctId: string
  event: EventName
  properties: AdminAnalyticsProperties
  groups: AdminAnalyticsGroup
}

export type BuildAdminAnalyticsCaptureParams<
  EventName extends AdminAnalyticsEventName
> = {
  distinctId: string
  event: EventName
  organizationId: number | string
  organizationName?: string | null
  environment?: string | null
  userId?: string | null
  app?: AnalyticsApplication
  surface?: AnalyticsSurface
  properties: AdminAnalyticsEventProperties<EventName>
}

export const ADMIN_ANALYTICS_COMMON_PROPERTIES = {
  app: 'web',
  surface: 'admin'
} as const

export const ADMIN_ANALYTICS_GROUP_TYPE = 'organization'

const inferAnalyticsUserId = (
  distinctId: string,
  userId?: string | null
): string | null => {
  if (userId !== undefined) {
    return userId
  }

  return distinctId.startsWith('org_') ? null : distinctId
}

export function buildAdminAnalyticsCapture<
  EventName extends AdminAnalyticsEventName
>({
  distinctId,
  event,
  organizationId,
  organizationName,
  environment,
  userId,
  app = 'web',
  surface = 'admin',
  properties
}: BuildAdminAnalyticsCaptureParams<EventName>): AdminAnalyticsCapture<EventName> {
  const normalizedOrganizationId = Number(organizationId)

  return {
    distinctId,
    event,
    groups: {
      [ADMIN_ANALYTICS_GROUP_TYPE]: String(organizationId)
    } as AdminAnalyticsGroup,
    properties: {
      ...ADMIN_ANALYTICS_COMMON_PROPERTIES,
      app,
      surface,
      organization_id: Number.isFinite(normalizedOrganizationId)
        ? normalizedOrganizationId
        : null,
      organization_name: organizationName ?? null,
      environment: environment ?? null,
      user_id: inferAnalyticsUserId(distinctId, userId),
      ...properties
    }
  }
}
